import type { AppendMessage, CreateAppendMessage } from '@assistant-ui/react'

import { audioFormat } from '@/types/chat-v2/messages'
import type {
  AudioMediaType,
  ImageMediaType,
  NewUserContent,
  UserContent,
  UserPromptPart,
  VideoMediaType,
} from '@/types/chat-v2/messages'

export const CHAT_AUTO_CONTINUATION_METADATA_KEY = 'sweetspot_auto_continuation'
// Mirrors REMINDER_PART_METADATA_KEY in common_services chat_services/synthetic_parts.py.
export const SYSTEM_REMINDER_METADATA_KEY = 'sweetspot_reminder'
export const CHAT_AUTO_CONTINUATION_PARENT_RUN_ID_KEY =
  'sweetspot_auto_continuation_parent_run_id'

export const CHAT_AUTO_CONTINUATION_PROMPT = `<system-notice>
Continue.

- You MUST resume the most recent intent and carry the unfinished work to completion.
- Interrupted mid-step? Pick it back up from where it stopped.
- You NEVER pause to summarize progress, re-confirm the plan, or ask whether to proceed — just continue.
</system-notice>`

export const buildChatAutoContinuationContent = (): NewUserContent[] => [
  {
    kind: 'text-content',
    content: CHAT_AUTO_CONTINUATION_PROMPT,
    metadata: { [CHAT_AUTO_CONTINUATION_METADATA_KEY]: true },
  },
]

type ChatAutoContinuationMessage = Exclude<CreateAppendMessage, string>

export const createChatAutoContinuationMessage = (
  parentId: string,
  parentRunId: string,
): ChatAutoContinuationMessage => ({
  parentId,
  role: 'user',
  content: [],
  metadata: {
    custom: {
      [CHAT_AUTO_CONTINUATION_METADATA_KEY]: true,
      // The failed run's id travels explicitly. Re-deriving it from the
      // `parentId` message-id string breaks whenever that id is not
      // `<runId>:<n>`-shaped (optimistic/interrupt-overlay ids), which would
      // silently rebase the continuation onto the session's current run.
      [CHAT_AUTO_CONTINUATION_PARENT_RUN_ID_KEY]: parentRunId,
    },
  },
})

export const isChatAutoContinuationMessage = (message: {
  metadata?: AppendMessage['metadata']
}): boolean => {
  const custom = message.metadata?.custom
  return (
    typeof custom === 'object' &&
    custom !== null &&
    !Array.isArray(custom) &&
    CHAT_AUTO_CONTINUATION_METADATA_KEY in custom &&
    custom[CHAT_AUTO_CONTINUATION_METADATA_KEY] === true
  )
}

export const chatAutoContinuationParentRunId = (message: {
  metadata?: AppendMessage['metadata']
}): string | null => {
  if (!isChatAutoContinuationMessage(message)) return null
  const custom = message.metadata?.custom as Record<string, unknown>
  const parentRunId = custom[CHAT_AUTO_CONTINUATION_PARENT_RUN_ID_KEY]
  return typeof parentRunId === 'string' ? parentRunId : null
}

export const isChatAutoContinuationContent = (
  content: UserContent,
): boolean => {
  if (typeof content !== 'object' || content.kind !== 'text-content') {
    return false
  }

  const metadata = content.metadata
  return (
    typeof metadata === 'object' &&
    metadata !== null &&
    CHAT_AUTO_CONTINUATION_METADATA_KEY in metadata &&
    metadata[CHAT_AUTO_CONTINUATION_METADATA_KEY] === true
  )
}

/** A reminder block the backend adds for the model; the user never wrote it. */
export const isSystemReminderPart = (part: UserPromptPart): boolean => {
  if (typeof part.content === 'string' || part.content.length !== 1) {
    return false
  }
  const [content] = part.content
  if (typeof content !== 'object' || content.kind !== 'text-content') {
    return false
  }
  const metadata = content.metadata
  return (
    typeof metadata === 'object' &&
    metadata !== null &&
    SYSTEM_REMINDER_METADATA_KEY in metadata &&
    metadata[SYSTEM_REMINDER_METADATA_KEY] != null
  )
}

// Compute file identifiers matching pydantic-ai's _multi_modal_content_identifier.
async function computeFileIdentifier(value: string): Promise<string> {
  const data = new TextEncoder().encode(value)
  const hashBuf = await crypto.subtle.digest('SHA-1', data)
  const hashArr = new Uint8Array(hashBuf)
  const hex = [...hashArr].map((b) => b.toString(16).padStart(2, '0')).join('')
  return hex.slice(0, 6)
}

export type ThreadAudioFormat = Extract<
  AppendMessage['content'][number],
  { type: 'audio' }
>['audio']['format']

export type ThreadFilePart = Extract<
  AppendMessage['content'][number],
  { type: 'file' }
>

export function audioMediaTypeFromThreadFormat(
  format: ThreadAudioFormat,
): AudioMediaType {
  switch (format) {
    case 'mp3':
      return 'audio/mpeg'
    case 'wav':
      return 'audio/wav'
    default: {
      const _exhaustiveCheck: never = format
      throw new Error(`unsupported audio format ${_exhaustiveCheck}`)
    }
  }
}

export function threadAudioFormatFromMediaType(
  mediaType: string,
): ThreadAudioFormat | null {
  try {
    const format = audioFormat(mediaType)
    return format === 'mp3' || format === 'wav' ? format : null
  } catch {
    return null
  }
}

function normalizeThreadMediaType(mediaType: string): string {
  return mediaType.split(';', 1)[0]?.trim().toLowerCase() ?? ''
}

function isImageMediaType(mediaType: string): mediaType is ImageMediaType {
  return (
    mediaType === 'image/jpeg' ||
    mediaType === 'image/png' ||
    mediaType === 'image/gif' ||
    mediaType === 'image/webp'
  )
}

function isAudioMediaType(mediaType: string): mediaType is AudioMediaType {
  return (
    mediaType === 'audio/mpeg' ||
    mediaType === 'audio/wav' ||
    mediaType === 'audio/ogg' ||
    mediaType === 'audio/flac' ||
    mediaType === 'audio/aiff' ||
    mediaType === 'audio/aac'
  )
}

function isVideoMediaType(mediaType: string): mediaType is VideoMediaType {
  return (
    mediaType === 'video/x-matroska' ||
    mediaType === 'video/quicktime' ||
    mediaType === 'video/mp4' ||
    mediaType === 'video/webm' ||
    mediaType === 'video/x-flv' ||
    mediaType === 'video/mpeg' ||
    mediaType === 'video/x-ms-wmv' ||
    mediaType === 'video/3gpp'
  )
}

export function newUserContentFromThreadFilePart(
  part: ThreadFilePart,
  imageIdentifierText?: string,
): NewUserContent | NewUserContent[] {
  const mediaType = normalizeThreadMediaType(part.mimeType)

  if (isImageMediaType(mediaType)) {
    const imageContent: NewUserContent = {
      url: part.data,
      kind: 'image-url',
      media_type: mediaType,
    }
    return imageIdentifierText
      ? [imageIdentifierText, imageContent]
      : imageContent
  }

  if (isAudioMediaType(mediaType)) {
    return {
      url: part.data,
      kind: 'audio-url',
      media_type: mediaType,
    }
  }

  if (isVideoMediaType(mediaType)) {
    return {
      url: part.data,
      kind: 'video-url',
      media_type: mediaType,
    }
  }

  if (mediaType.startsWith('image/')) {
    console.warn(
      '[chat-content] unsupported image file MIME type; sending as document-url',
      { mediaType },
    )
  }

  return mediaType
    ? { url: part.data, kind: 'document-url', media_type: mediaType }
    : { url: part.data, kind: 'document-url' }
}

async function contentFromThreadFilePart(
  part: ThreadFilePart,
): Promise<NewUserContent | NewUserContent[]> {
  const mediaType = normalizeThreadMediaType(part.mimeType)
  if (!isImageMediaType(mediaType)) {
    return newUserContentFromThreadFilePart(part)
  }

  const id = await computeFileIdentifier(part.data)
  return newUserContentFromThreadFilePart(part, `This is file ${id}:`)
}

type UserContentMessage = Pick<AppendMessage, 'content'> & {
  metadata?: AppendMessage['metadata']
}

export async function buildNewUserContent(
  message: UserContentMessage,
): Promise<NewUserContent[]> {
  // Only explicitly opted-in content metadata is persisted, never runtime state.
  const metadata = message.metadata?.custom?.userContentMetadata
  const content = await Promise.all(
    message.content.map(
      async (part): Promise<NewUserContent | NewUserContent[]> => {
        switch (part.type) {
          case 'text':
            return metadata === undefined
              ? part.text
              : { kind: 'text-content', content: part.text, metadata }
          case 'image': {
            const id = await computeFileIdentifier(part.image)
            return [
              `This is file ${id}:`,
              {
                url: part.image,
                kind: 'image-url',
              },
            ]
          }
          case 'file':
            return contentFromThreadFilePart(part)
          case 'audio':
            return {
              url: part.audio.data,
              media_type: audioMediaTypeFromThreadFormat(part.audio.format),
              kind: 'audio-url',
            }
          default:
            throw new Error(
              `Bad content part for request: ${String((part as { type?: unknown }).type)}`,
            )
        }
      },
    ),
  )

  return content.flat()
}

export async function buildCreateMessageContent(
  message: string | UserContentMessage,
): Promise<string | NewUserContent[]> {
  if (typeof message === 'string') {
    return message
  }

  return buildNewUserContent(message)
}
